import { useState } from "react";
import { Plus, Trash2, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { SectionTitle, Field, Pill } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none focus:border-lime";

const BLOCK_LABELS = {
  text: "Text",
  visual: "Visual",
  product_board: "Product Board",
  selection_group: "Selections Requiring Approval",
};

const uid = () => Math.random().toString(36).slice(2, 10);

export default function PresentationComposer({ presentationId, visuals = [], selections = [], initial = [] }) {
  const [blocks, setBlocks] = useState(initial);
  const [dirty, setDirty] = useState(false);

  const update = (next) => {
    setBlocks(next);
    setDirty(true);
  };

  const add = (type) =>
    update([...blocks, { id: uid(), type, heading: "", body: "", visual_id: visuals[0]?.id || null, selection_ids: [] }]);

  const move = (idx, dir) => {
    const next = [...blocks];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    update(next);
  };

  const save = async () => {
    try {
      await api.put(`/presentations/${presentationId}/blocks`, { blocks });
      setDirty(false);
      toast.success("Presentation order saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <section className="card-surface p-7" data-testid="presentation-composer">
      <SectionTitle
        action={
          <button
            type="button"
            data-testid="composer-save-btn"
            onClick={save}
            disabled={!dirty}
            className="rounded-sm bg-lime px-4 py-2 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-50"
          >
            {dirty ? "Save order" : "Saved"}
          </button>
        }
      >
        Presentation Composer
      </SectionTitle>
      <p className="max-w-2xl text-xs leading-relaxed text-fog">
        Arrange the story your client walks through. Leave this empty and the presentation falls back
        to the classic order: cover, narrative, visuals, then selections.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {Object.entries(BLOCK_LABELS).map(([type, label]) => (
          <button
            key={type}
            type="button"
            data-testid={`composer-add-${type}`}
            onClick={() => add(type)}
            className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs text-stone transition-colors hover:border-lime"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add {label}
          </button>
        ))}
      </div>

      {blocks.length === 0 ? (
        <p className="mt-7 text-sm text-fog" data-testid="composer-empty">No blocks yet — the classic layout will be used.</p>
      ) : (
        <div className="mt-7 space-y-4" data-testid="composer-blocks">
          {blocks.map((b, idx) => (
            <div key={b.id} className="rounded-sm border border-line bg-white p-5" data-testid={`composer-block-${b.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <GripVertical className="h-4 w-4 text-fog" strokeWidth={1.5} />
                  <Pill tone="bg-[#EDEFC4] text-ink">{BLOCK_LABELS[b.type]}</Pill>
                </div>
                <div className="flex gap-1">
                  <button type="button" data-testid={`composer-up-${b.id}`} onClick={() => move(idx, -1)} className="rounded-sm border border-line p-1.5 text-stone hover:border-lime">
                    <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button type="button" data-testid={`composer-down-${b.id}`} onClick={() => move(idx, 1)} className="rounded-sm border border-line p-1.5 text-stone hover:border-lime">
                    <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button type="button" data-testid={`composer-remove-${b.id}`} onClick={() => update(blocks.filter((x) => x.id !== b.id))} className="rounded-sm border border-line p-1.5 text-stone hover:border-clay hover:text-clay">
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {b.type === "text" && (
                <div className="mt-4 grid gap-4">
                  <Field label="Heading (optional)">
                    <input data-testid={`composer-heading-${b.id}`} value={b.heading || ""} onChange={(e) => update(blocks.map((x) => (x.id === b.id ? { ...x, heading: e.target.value } : x)))} className={input} />
                  </Field>
                  <Field label="Text">
                    <textarea data-testid={`composer-body-${b.id}`} rows={3} value={b.body || ""} onChange={(e) => update(blocks.map((x) => (x.id === b.id ? { ...x, body: e.target.value } : x)))} className={input} />
                  </Field>
                </div>
              )}

              {b.type === "visual" && (
                <Field label="Which visual?" className="mt-4">
                  <select data-testid={`composer-visual-${b.id}`} value={b.visual_id || ""} onChange={(e) => update(blocks.map((x) => (x.id === b.id ? { ...x, visual_id: e.target.value } : x)))} className={input}>
                    <option value="">Select…</option>
                    {visuals.map((v) => (
                      <option key={v.id} value={v.id}>{v.type}{v.title ? ` — ${v.title}` : ""}</option>
                    ))}
                  </select>
                </Field>
              )}

              {b.type === "selection_group" && (
                <div className="mt-4">
                  <div className="overline mb-3">Include these selections (leave empty for all)</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {selections.map((s) => {
                      const on = (b.selection_ids || []).includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`composer-selection-${b.id}-${s.id}`}
                          onClick={() =>
                            update(blocks.map((x) => (x.id === b.id
                              ? { ...x, selection_ids: on ? x.selection_ids.filter((i) => i !== s.id) : [...(x.selection_ids || []), s.id] }
                              : x)))
                          }
                          className={`rounded-sm border px-3 py-2 text-left text-xs transition-colors ${on ? "border-lime bg-[#EDEFC4]" : "border-line hover:border-fog"}`}
                        >
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {b.type === "product_board" && (
                <p className="mt-4 text-xs text-fog">Shows every selection in this presentation as a visual product board.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
